import {
  ContractorStatus,
  ContractorType,
  ExpirationIndicator,
} from 'src/constants/contractor/enums'
import type { CollectedData } from 'src/interfaces/collectedData'

export { ExpirationIndicator }

/**
 * One expiring document, returned in the same shape as a collected data row on the contractor and
 * onboarding detail screens, so the drawer renders it with the shared `ContractorStepDetailsCard`.
 *
 * The backend fills only the fields that card reads: `documents` always comes back empty and the
 * rejection and review fields null, because the queue shows the step read-only.
 */
export type ExpiringDocument = CollectedData

/** Right-hand panel of the contact drawer — mirrors the two tabs in the original sketch. */
export enum ContactDrawerTab {
  General = 'general',
  Contacts = 'contacts',
}

/** How the last external touch happened — drives the icon in the priority queue. */
export type ContactChannel = 'note' | 'call' | 'text'

/** Windows offered by the "Exclude contacted" filter — inclusive of their last day. */
export enum ExcludeContactedWindow {
  Today = 'today',
  ThreeDays = '3d',
  SevenDays = '7d',
  FourteenDays = '14d',
}

/** The grid's "Last external contact" cell — the list endpoint carries only this much. */
export interface LastExternalContact {
  channel: ContactChannel
  at: string
}

/** A single logged contact, as returned by the detail endpoint and by logging one. */
export interface Contact {
  id: string
  channel: ContactChannel
  note: string | null
  createdAt: string
  author: { id: string; name: string; email: string }
}

/** A contractor as `Endpoints.FetchPriorityQueue` returns them. */
export interface PriorityQueueRow {
  /** Internal id — what the drawer's detail/contact endpoints take as `:contractorId`. */
  id: string
  /** The externalId shown to users as "Contractor ID". */
  contractorId: string
  name: string
  contractorStatus: ContractorStatus
  tags: string[]
  expirationIndicator: ExpirationIndicator
  /** Soonest qualifying document's expiry. */
  expiresAt: string
  phoneNumber: string | null
  /** Assigned template name, or `null` when none is assigned. */
  onboardingFlow: string | null
  lastExternalContact: LastExternalContact | null
}

export interface PriorityQueuePagination {
  limit: number
  offset: number
  total: number
}

/** The response envelope of `Endpoints.FetchPriorityQueue`. */
export interface PriorityQueueListResponse {
  data: PriorityQueueRow[]
  pagination: PriorityQueuePagination
}

/** Grid row: the wire row plus fields derived from `expiresAt` for display and filtering. */
export interface PriorityQueueGridRow extends PriorityQueueRow {
  daysLeft: number
}

/** The response envelope of `Endpoints.FetchPriorityQueueDetail`. */
export interface PriorityQueueDetail {
  id: string
  contractorId: string
  name: string
  status: ContractorStatus
  contractorType: ContractorType
  phoneNumber: string | null
  email: string | null
  /** Every qualifying document, soonest first. */
  expiringDocuments: ExpiringDocument[]
  /** Full contact history, most recent first. */
  contacts: Contact[]
}

/** The response envelope of `Endpoints.FetchPriorityQueueFilterOptions`. */
export interface PriorityQueueFilterOptions {
  templates: { id: string; name: string }[]
  tags: string[]
}

/** Body of `Endpoints.CreatePriorityQueueContact` — the author is stamped server-side. */
export interface CreateContactParams {
  contractorId: string
  channel: ContactChannel
  note?: string
}
